/**
 * Custom Mega Menu
 * Handles L2 category switching and L3 product grid display
 */
class CustomMegaMenu {
  constructor() {
    this.init();
  }

  init() {
    this.setupDesktopMenu();
    this.setupMobileMenu();
    this.setupCloseButtons();
    this.setupKeyboardNavigation();
    this.setupMegaMenuToggle();
    this.setupDrawerTabs();
    this.preventDrawerClose();
    this.setupOverlay();
  }

  /**
   * Setup desktop menu interactions
   */
  setupDesktopMenu() {
    // Handle category switching in left menu
    const categoryLinks = document.querySelectorAll('.mega-menu-category[data-category]');
    
    categoryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        // Check if this is a category switch (not a navigation)
        const isActive = link.classList.contains('active');
        
        // If the category is already active, allow navigation to the category page
        if (isActive) {
          // Let the link navigate normally
          return;
        }
        
        // Otherwise, prevent navigation and switch category
        e.preventDefault();
        this.switchCategory(link);
      });
    });

    // Initialize first category as active
    this.initializeDefaultCategory();
  }

  /**
   * Setup mobile menu interactions
   */
  setupMobileMenu() {
    // Handle mobile category toggles
    const mobileToggles = document.querySelectorAll('.mega-menu-mobile-toggle');
    
    mobileToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileCategory(toggle);
      });
    });

    // Handle mobile category links (direct links without subcategories)
    const mobileLinks = document.querySelectorAll('.mega-menu-mobile-category a:not(.mega-menu-mobile-toggle)');
    
    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        // Let direct links navigate normally
        this.closeMobileMenu();
      });
    });
  }

  /**
   * Setup close buttons
   */
  setupCloseButtons() {
    const closeButtons = document.querySelectorAll('.mega-menu-close');
    
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileMenu();
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      // Don't close if clicking a tab button
      if (e.target.classList.contains('mega-menu-drawer__tab-btn')) {
        return;
      }
      
      const megaMenu = e.target.closest('.custom-mega-menu');
      const isInsideMenu = e.target.closest('.custom-mega-menu__content');
      const isInsideDrawer = e.target.closest('.menu-drawer');
      
      if (!megaMenu && !isInsideMenu && !isInsideDrawer) {
        this.closeAllMenus();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllMenus();
      }
    });
  }

  /**
   * Setup mega menu toggle events
   */
  setupMegaMenuToggle() {
    // Handle when mega menu opens
    const megaMenus = document.querySelectorAll('.custom-mega-menu details');
    
    megaMenus.forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          // Initialize the active state when mega menu opens
          const container = details.querySelector('.mega-menu-container');
          if (container) {
            this.initializeContainerDefaults(container);
          }
        }
      });
    });
  }

  /**
   * Initialize defaults for a specific container
   */
  initializeContainerDefaults(container) {
    const firstCategoryLink = container.querySelector('.mega-menu-category[data-category]');
    if (firstCategoryLink) {
      // Ensure first category is marked as active
      const allCategories = container.querySelectorAll('.mega-menu-category');
      allCategories.forEach(cat => cat.classList.remove('active'));
      firstCategoryLink.classList.add('active');
      
      // Show first products
      const categoryId = firstCategoryLink.getAttribute('data-category');
      const allProducts = container.querySelectorAll('.mega-menu-products');
      allProducts.forEach(products => products.classList.remove('active'));
      
      const firstProducts = container.querySelector(`[data-category="${categoryId}"]`);
      if (firstProducts && firstProducts.classList.contains('mega-menu-products')) {
        firstProducts.classList.add('active');
      }
    }
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    // Handle keyboard navigation for category links
    const categoryLinks = document.querySelectorAll('.mega-menu-category');
    
    categoryLinks.forEach(link => {
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });
    });

    // Make category links focusable
    categoryLinks.forEach(link => {
      if (!link.hasAttribute('tabindex')) {
        link.setAttribute('tabindex', '0');
      }
    });
  }

  /**
   * Switch active category and show corresponding products
   */
  switchCategory(activeLink) {
    const menuContainer = activeLink.closest('.mega-menu-container');
    if (!menuContainer) return;

    const categoryId = activeLink.getAttribute('data-category');
    if (!categoryId) return;

    // Remove active class from all category links
    const allLinks = menuContainer.querySelectorAll('.mega-menu-category');
    allLinks.forEach(link => link.classList.remove('active'));

    // Add active class to clicked link
    activeLink.classList.add('active');

    // Hide all product sections
    const allProducts = menuContainer.querySelectorAll('.mega-menu-products');
    allProducts.forEach(products => {
      products.classList.remove('active');
    });

    // Show target product section
    const targetProducts = menuContainer.querySelector(`.mega-menu-products[data-category="${categoryId}"]`);
    
    if (targetProducts) {
      targetProducts.classList.add('active');
      
      // Animate in
      targetProducts.style.opacity = '0';
      requestAnimationFrame(() => {
        targetProducts.style.opacity = '1';
      });
    }
  }

  /**
   * Initialize first category as active
   */
  initializeDefaultCategory() {
    // Wait a bit for the DOM to be fully ready
    setTimeout(() => {
      const megaMenuContainers = document.querySelectorAll('.mega-menu-container');
      
      megaMenuContainers.forEach(container => {
        // First, hide all product sections
        const allProducts = container.querySelectorAll('.mega-menu-products');
        allProducts.forEach(products => products.classList.remove('active'));
        
        // Remove active from all category links
        const allLinks = container.querySelectorAll('.mega-menu-category');
        allLinks.forEach(link => link.classList.remove('active'));
        
        // Now activate the first category
        const firstCategoryLink = container.querySelector('.mega-menu-category[data-category]');
        if (firstCategoryLink) {
          firstCategoryLink.classList.add('active');
          
          const categoryId = firstCategoryLink.getAttribute('data-category');
          const firstProducts = container.querySelector(`.mega-menu-products[data-category="${categoryId}"]`);
          
          if (firstProducts) {
            firstProducts.classList.add('active');
          }
        }
      });
    }, 100);
  }

  /**
   * Toggle mobile category section
   */
  toggleMobileCategory(toggle) {
    const categoryId = toggle.getAttribute('data-category');
    if (!categoryId) return;

    const mobileContent = toggle.closest('.mega-menu-mobile-content');
    if (!mobileContent) return;

    const targetProducts = mobileContent.querySelector(`[data-category="${categoryId}"]`);
    if (!targetProducts) return;

    const isCurrentlyOpen = targetProducts.style.display === 'block';

    // Close all other categories
    const allProducts = mobileContent.querySelectorAll('.mega-menu-mobile-products');
    allProducts.forEach(products => {
      products.style.display = 'none';
    });

    // Remove active class from all toggles
    const allToggles = mobileContent.querySelectorAll('.mega-menu-mobile-toggle');
    allToggles.forEach(t => t.classList.remove('active'));

    // Toggle current category
    if (!isCurrentlyOpen) {
      targetProducts.style.display = 'block';
      toggle.classList.add('active');
    }
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu() {
    const openMenus = document.querySelectorAll('.custom-mega-menu[open]');
    openMenus.forEach(menu => {
      menu.removeAttribute('open');
    });

    // Remove mobile menu active class from body if exists
    document.body.classList.remove('mobile-mega-menu-active');
  }

  /**
   * Close all menus
   */
  closeAllMenus() {
    this.closeMobileMenu();
    
    // Close any open details elements
    const openDetails = document.querySelectorAll('.custom-mega-menu[open]');
    openDetails.forEach(details => {
      details.removeAttribute('open');
    });
  }

  /**
   * Setup drawer tabs functionality
   */
  setupDrawerTabs() {
    // Use event delegation to handle dynamically created tab buttons
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('mega-menu-drawer__tab-btn')) {
        // Check if this tab is already active
        const isActive = e.target.classList.contains('active');
        
        // If the tab is already active, allow navigation to the category page
        if (isActive) {
          // Let the link navigate normally
          return;
        }
        
        // Otherwise, prevent navigation and switch tab
        e.preventDefault();
        e.stopPropagation(); // Prevent bubbling to drawer close handlers
        e.stopImmediatePropagation(); // Stop all other listeners
        this.switchDrawerTab(e.target);
      }
    }, true); // Use capture phase to catch events before they bubble

    // Also setup any existing tab buttons with high priority
    const tabButtons = document.querySelectorAll('.mega-menu-drawer__tab-btn');
    
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // Check if this tab is already active
        const isActive = button.classList.contains('active');
        
        // If the tab is already active, allow navigation to the category page
        if (isActive) {
          // Let the link navigate normally
          return;
        }
        
        // Otherwise, prevent navigation and switch tab
        e.preventDefault();
        e.stopPropagation(); // Prevent bubbling to drawer close handlers
        e.stopImmediatePropagation(); // Stop all other listeners
        this.switchDrawerTab(button);
      }, true); // Use capture phase
      
      // Remove the type="button" since it's now an anchor
      // button.setAttribute('type', 'button');
      
      // Prevent any default details behavior on mousedown only for non-active tabs
      button.addEventListener('mousedown', (e) => {
        const isActive = button.classList.contains('active');
        if (!isActive) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });
  }

  /**
   * Switch drawer tab
   */
  switchDrawerTab(activeButton) {
    const targetId = activeButton.getAttribute('data-tab-target');
    if (!targetId) return;

    // Find the mega menu content container that holds both categories and products
    const megaMenuContent = activeButton.closest('.mega-menu-drawer__content');
    if (!megaMenuContent) return;

    // Remove active class from all tab buttons in this mega menu
    const allTabButtons = megaMenuContent.querySelectorAll('.mega-menu-drawer__tab-btn');
    allTabButtons.forEach(btn => btn.classList.remove('active'));

    // Hide all tab panels in this mega menu
    const allTabPanels = megaMenuContent.querySelectorAll('.mega-menu-drawer__tab-panel');
    allTabPanels.forEach(panel => panel.classList.remove('active'));

    // Activate clicked button
    activeButton.classList.add('active');

    // Show target tab panel
    const targetPanel = megaMenuContent.querySelector(`#${targetId}`);
    if (targetPanel) {
      targetPanel.classList.add('active');
    }
  }

  /**
   * Prevent drawer from closing when clicking inside mega menu
   */
  preventDrawerClose() {
    // Prevent details element from closing when clicking inside mega menu drawer
    document.addEventListener('click', (e) => {
      const megaMenuDrawer = e.target.closest('.mega-menu-drawer__content');
      if (megaMenuDrawer) {
        // Allow links to work normally - don't prevent default for anchor tags
        if (e.target.tagName === 'A' || e.target.closest('a')) {
          return; // Let the link work normally
        }
        
        const detailsElement = megaMenuDrawer.closest('details');
        if (detailsElement) {
          // Prevent the details element from toggling closed
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Ensure details stays open
          detailsElement.setAttribute('open', '');
        }
      }
    }, true); // Use capture phase
    
    // Also prevent on mousedown for extra safety
    document.addEventListener('mousedown', (e) => {
      const megaMenuDrawer = e.target.closest('.mega-menu-drawer__content');
      if (megaMenuDrawer) {
        e.stopPropagation();
      }
    }, true);
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Reset mobile menu state on resize
    const mobileProducts = document.querySelectorAll('.mega-menu-mobile-products');
    mobileProducts.forEach(products => {
      products.style.display = 'none';
    });

    const mobileToggles = document.querySelectorAll('.mega-menu-mobile-toggle');
    mobileToggles.forEach(toggle => {
      toggle.classList.remove('active');
    });

    // Ensure first desktop category is active
    this.initializeDefaultCategory();
  }

  /**
   * Setup overlay functionality
   */
  setupOverlay() {
    this.overlay = document.querySelector('.custom-mega-menu-overlay');
    this.detailsMenus = document.querySelectorAll('.custom-mega-menu');

    if (!this.overlay) return;

    // Listen for menu toggle events
    this.detailsMenus.forEach(details => {
      details.addEventListener('toggle', () => {
        this.updateOverlay();
      });
    });

    // Close menu when overlay is clicked
    this.overlay.addEventListener('click', () => {
      this.closeAllMenus();
      this.hideOverlay();
    });
  }

  /**
   * Update overlay visibility based on menu state
   */
  updateOverlay() {
    if (!this.overlay) return;

    const anyOpen = Array.from(this.detailsMenus).some(d => d.open);
    if (anyOpen) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  /**
   * Show overlay
   */
  showOverlay() {
    if (this.overlay) {
      this.overlay.classList.add('active');
    }
  }

  /**
   * Hide overlay
   */
  hideOverlay() {
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const megaMenu = new CustomMegaMenu();
  
  // Handle window resize
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      megaMenu.handleResize();
    }, 250);
  });
});

// Export for use in other scripts if needed
window.CustomMegaMenu = CustomMegaMenu; 